import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { Credential, CredentialCategory, CredentialType } from '../types';
import { 
  X, Key, Server, Database, Cloud, Network, Box, Settings, 
  Eye, EyeOff, RefreshCw, Lock, User, Globe, Hash, FileKey,
  Sparkles, Info, Calendar, Tag, FileText, Copy, Check
} from 'lucide-react';
import toast from 'react-hot-toast';
import './CredentialModal.css';

interface CredentialModalProps {
  credential?: Credential | null;
  onClose: () => void;
  onSuccess: () => void;
}

// Category configurations with smart defaults
const categoryConfig: Record<CredentialCategory, {
  icon: React.ReactNode;
  color: string;
  defaultType: CredentialType;
  suggestedFields: string[];
  placeholder: {
    name: string;
    host?: string;
    username?: string;
  };
}> = {
  VIRTUAL_MACHINE: {
    icon: <Server size={20} />,
    color: '#3b82f6',
    defaultType: 'SSH_KEY',
    suggestedFields: ['host', 'username', 'port'],
    placeholder: {
      name: 'e.g., Ubuntu VM - Production',
      host: '192.168.1.100',
      username: 'ubuntu'
    }
  },
  APPLICATION: {
    icon: <Box size={20} />,
    color: '#8b5cf6',
    defaultType: 'USERNAME_PASSWORD',
    suggestedFields: ['username', 'password'],
    placeholder: {
      name: 'e.g., Jira Admin Account',
      username: 'admin'
    }
  },
  NETWORK_DEVICE: {
    icon: <Network size={20} />,
    color: '#10b981',
    defaultType: 'USERNAME_PASSWORD',
    suggestedFields: ['host', 'username', 'port'],
    placeholder: {
      name: 'e.g., Cisco Switch - Main Office',
      host: '192.168.1.1',
      username: 'admin'
    }
  },
  SERVER: {
    icon: <Server size={20} />,
    color: '#f59e0b',
    defaultType: 'SSH_KEY',
    suggestedFields: ['host', 'username', 'port'],
    placeholder: {
      name: 'e.g., Web Server - Production',
      host: 'server.example.com',
      username: 'root'
    }
  },
  CLOUD_SERVICE: {
    icon: <Cloud size={20} />,
    color: '#06b6d4',
    defaultType: 'API_TOKEN',
    suggestedFields: ['host', 'token'],
    placeholder: {
      name: 'e.g., AWS Access Key',
      host: 'api.aws.amazon.com'
    }
  },
  DATABASE: {
    icon: <Database size={20} />,
    color: '#ec4899',
    defaultType: 'USERNAME_PASSWORD',
    suggestedFields: ['host', 'username', 'port'],
    placeholder: {
      name: 'e.g., PostgreSQL - Production DB',
      host: 'db.example.com',
      username: 'postgres'
    }
  },
  OTHER: {
    icon: <Settings size={20} />,
    color: '#64748b',
    defaultType: 'USERNAME_PASSWORD',
    suggestedFields: ['username', 'password'],
    placeholder: {
      name: 'e.g., Custom Service',
      username: 'user'
    }
  }
};

const CredentialModal: React.FC<CredentialModalProps> = ({ credential, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'VIRTUAL_MACHINE' as CredentialCategory,
    type: 'USERNAME_PASSWORD' as CredentialType,
    username: '',
    password: '',
    host: '',
    port: '',
    notes: '',
    tags: '',
    expiresAt: '',
    certificate: '',
    keyFile: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [generatingPassword, setGeneratingPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (credential) {
      setFormData({
        name: credential.name || '',
        description: credential.description || '',
        category: credential.category,
        type: credential.type,
        username: credential.data?.username || '',
        password: credential.data?.password || '',
        host: credential.data?.host || '',
        port: credential.data?.port?.toString() || '',
        notes: credential.notes || '',
        tags: credential.tags?.join(', ') || '',
        expiresAt: credential.expiresAt ? credential.expiresAt.split('T')[0] : '',
        certificate: credential.data?.certificate || '',
        keyFile: credential.data?.key || '',
      });
    } else {
      // Set smart defaults based on category
      updateCategoryDefaults('VIRTUAL_MACHINE');
    }
  }, [credential]);

  const updateCategoryDefaults = (category: CredentialCategory) => {
    const config = categoryConfig[category];
    setFormData(prev => ({
      ...prev,
      category,
      type: config.defaultType,
      host: '',
      port: category === 'DATABASE' ? '5432' : category === 'NETWORK_DEVICE' ? '22' : '',
    }));
  };

  const handleCategoryChange = (category: CredentialCategory) => {
    updateCategoryDefaults(category);
  };

  const generatePassword = async () => {
    setGeneratingPassword(true);
    try {
      const response = await api.get('/utils/generate-password?length=16');
      const generatedPassword = response.data.password;
      setFormData({ ...formData, password: generatedPassword });
      toast.success('Password generated successfully!');
    } catch (error: any) {
      toast.error('Failed to generate password');
    } finally {
      setGeneratingPassword(false);
    }
  };

  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.success(`${fieldName} copied to clipboard!`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const createMutation = useMutation(
    async (data: any) => {
      const response = await api.post('/credentials', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Credential created successfully');
        onSuccess();
      },
      onError: (error: any) => {
        console.error('Create credential error:', error);
        let errorMessage = 'Failed to create credential';
        
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const errorMessages = error.response.data.errors.map((e: any) => {
            const field = e.param || e.path || 'field';
            const msg = e.msg || e.message || 'Invalid value';
            return `${field}: ${msg}`;
          });
          errorMessage = errorMessages.join('; ');
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }
        
        toast.error(errorMessage, { duration: 5000 });
      },
    }
  );

  const updateMutation = useMutation(
    async (data: any) => {
      const response = await api.put(`/credentials/${credential!.id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Credential updated successfully');
        onSuccess();
      },
      onError: (error: any) => {
        console.error('Update credential error:', error);
        let errorMessage = 'Failed to update credential';
        
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const errorMessages = error.response.data.errors.map((e: any) => {
            const field = e.param || e.path || 'field';
            const msg = e.msg || e.message || 'Invalid value';
            return `${field}: ${msg}`;
          });
          errorMessage = errorMessages.join('; ');
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }
        
        toast.error(errorMessage, { duration: 5000 });
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    const category = (formData.category || 'VIRTUAL_MACHINE').replace(/\s+/g, '_').toUpperCase() as CredentialCategory;
    const type = (formData.type || 'USERNAME_PASSWORD').replace(/\s+/g, '_').toUpperCase() as CredentialType;

    const data: any = {
      name: formData.name.trim(),
      description: formData.description?.trim() || null,
      category: category,
      type: type,
      notes: formData.notes?.trim() || null,
      tags: formData.tags.split(',').map((t) => t.trim()).filter((t) => t),
      expiresAt: formData.expiresAt && formData.expiresAt.trim() 
        ? new Date(formData.expiresAt).toISOString() 
        : null,
    };

    // Build credential data based on type
    const credentialData: any = {};
    
    if (type === 'USERNAME_PASSWORD') {
      if (!formData.username?.trim()) {
        toast.error('Username is required');
        return;
      }
      if (!formData.password?.trim()) {
        toast.error('Password is required');
        return;
      }
      credentialData.username = formData.username.trim();
      credentialData.password = formData.password;
      if (formData.host?.trim()) credentialData.host = formData.host.trim();
      if (formData.port?.trim()) credentialData.port = parseInt(formData.port);
    } else if (type === 'SSH_KEY') {
      if (!formData.host?.trim()) {
        toast.error('Host is required for SSH Key');
        return;
      }
      credentialData.host = formData.host.trim();
      credentialData.port = formData.port ? parseInt(formData.port) : 22;
      credentialData.username = formData.username?.trim() || '';
      credentialData.key = formData.keyFile || formData.password;
    } else if (type === 'API_TOKEN') {
      if (!formData.password?.trim()) {
        toast.error('API Token is required');
        return;
      }
      credentialData.token = formData.password;
      if (formData.host?.trim()) credentialData.host = formData.host.trim();
    } else if (type === 'CERTIFICATE') {
      if (!formData.certificate?.trim()) {
        toast.error('Certificate is required');
        return;
      }
      credentialData.certificate = formData.certificate;
      credentialData.key = formData.keyFile || '';
      if (formData.host?.trim()) credentialData.host = formData.host.trim();
      if (formData.port?.trim()) credentialData.port = parseInt(formData.port);
    }

    data.data = credentialData;

    if (credential) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const categories: CredentialCategory[] = [
    'VIRTUAL_MACHINE',
    'APPLICATION',
    'NETWORK_DEVICE',
    'SERVER',
    'CLOUD_SERVICE',
    'DATABASE',
    'OTHER',
  ];

  const types: CredentialType[] = ['USERNAME_PASSWORD', 'SSH_KEY', 'API_TOKEN', 'CERTIFICATE'];
  const currentConfig = categoryConfig[formData.category];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content credential-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <div className="title-icon" style={{ backgroundColor: `${currentConfig.color}15`, color: currentConfig.color }}>
              {currentConfig.icon}
            </div>
            <div>
              <h2>{credential ? 'Edit Credential' : 'Create New Credential'}</h2>
              <p className="modal-subtitle">Securely store and manage your credentials</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Basic Information Section */}
          <div className="form-section">
            <div className="section-header">
              <Key size={18} />
              <h3>Basic Information</h3>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>
                  <Hash size={14} />
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder={currentConfig.placeholder.name}
                />
              </div>
              <div className="form-group">
                <label>
                  {currentConfig.icon}
                  Category *
                </label>
                <div className="category-select-wrapper">
                  <select
                    value={formData.category}
                    onChange={(e) => handleCategoryChange(e.target.value as CredentialCategory)}
                    required
                    className="category-select"
                    style={{ borderLeftColor: currentConfig.color }}
                  >
                    {categories.map((cat) => {
                      const config = categoryConfig[cat];
                      return (
                        <option key={cat} value={cat}>
                          {cat.replace(/_/g, ' ')}
                        </option>
                      );
                    })}
                  </select>
                  <div className="category-badge" style={{ backgroundColor: `${currentConfig.color}15`, color: currentConfig.color }}>
                    {currentConfig.icon}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <Lock size={14} />
                  Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as CredentialType })}
                  required
                >
                  {types.map((t) => (
                    <option key={t} value={t}>
                      {t.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>
                  <Calendar size={14} />
                  Expires At
                </label>
                <input
                  type="date"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <FileText size={14} />
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                placeholder="Optional description or notes about this credential"
              />
            </div>
          </div>

          {/* Credential Details Section */}
          <div className="form-section">
            <div className="section-header">
              <Lock size={18} />
              <h3>Credential Details</h3>
            </div>

            {formData.type === 'USERNAME_PASSWORD' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <User size={14} />
                      Username *
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      required
                      placeholder={currentConfig.placeholder.username || 'Enter username'}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <Lock size={14} />
                      Password *
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        placeholder="Enter password"
                        className="password-input"
                      />
                      <div className="password-actions">
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={() => setShowPassword(!showPassword)}
                          title={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={generatePassword}
                          disabled={generatingPassword}
                          title="Generate secure password"
                        >
                          <RefreshCw size={16} className={generatingPassword ? 'spinning' : ''} />
                        </button>
                        {formData.password && (
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => copyToClipboard(formData.password, 'Password')}
                            title="Copy password"
                          >
                            {copiedField === 'password' ? <Check size={16} /> : <Copy size={16} />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <Globe size={14} />
                      Host / URL
                    </label>
                    <input
                      type="text"
                      value={formData.host}
                      onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                      placeholder={currentConfig.placeholder.host || 'e.g., example.com or 192.168.1.1'}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <Hash size={14} />
                      Port
                    </label>
                    <input
                      type="number"
                      value={formData.port}
                      onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                      placeholder="e.g., 22, 80, 443, 3306"
                      min="1"
                      max="65535"
                    />
                  </div>
                </div>
              </>
            )}

            {formData.type === 'SSH_KEY' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <Globe size={14} />
                      Host *
                    </label>
                    <input
                      type="text"
                      value={formData.host}
                      onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                      required
                      placeholder={currentConfig.placeholder.host || 'e.g., server.example.com'}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <Hash size={14} />
                      Port
                    </label>
                    <input
                      type="number"
                      value={formData.port}
                      onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                      placeholder="22"
                      min="1"
                      max="65535"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>
                    <User size={14} />
                    Username
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder={currentConfig.placeholder.username || 'e.g., root, ubuntu'}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <FileKey size={14} />
                    SSH Private Key *
                  </label>
                  <textarea
                    value={formData.keyFile || formData.password}
                    onChange={(e) => {
                      if (formData.type === 'SSH_KEY') {
                        setFormData({ ...formData, keyFile: e.target.value, password: e.target.value });
                      }
                    }}
                    rows={8}
                    required
                    placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
                    className="code-textarea"
                  />
                  <div className="field-hint">
                    <Info size={12} />
                    <span>Paste your SSH private key here. It will be encrypted and stored securely.</span>
                  </div>
                </div>
              </>
            )}

            {formData.type === 'API_TOKEN' && (
              <>
                <div className="form-group">
                  <label>
                    <Key size={14} />
                    API Token *
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showToken ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      placeholder="Enter API token"
                      className="password-input"
                    />
                    <div className="password-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => setShowToken(!showToken)}
                        title={showToken ? 'Hide token' : 'Show token'}
                      >
                        {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      {formData.password && (
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={() => copyToClipboard(formData.password, 'Token')}
                          title="Copy token"
                        >
                          {copiedField === 'token' ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>
                    <Globe size={14} />
                    Host / Endpoint
                  </label>
                  <input
                    type="text"
                    value={formData.host}
                    onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                    placeholder={currentConfig.placeholder.host || 'e.g., api.example.com'}
                  />
                </div>
              </>
            )}

            {formData.type === 'CERTIFICATE' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <Globe size={14} />
                      Host
                    </label>
                    <input
                      type="text"
                      value={formData.host}
                      onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                      placeholder="e.g., example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <Hash size={14} />
                      Port
                    </label>
                    <input
                      type="number"
                      value={formData.port}
                      onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                      placeholder="e.g., 443"
                      min="1"
                      max="65535"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>
                    <FileKey size={14} />
                    Certificate *
                  </label>
                  <textarea
                    value={formData.certificate}
                    onChange={(e) => setFormData({ ...formData, certificate: e.target.value })}
                    rows={8}
                    required
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    className="code-textarea"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <FileKey size={14} />
                    Private Key
                  </label>
                  <textarea
                    value={formData.keyFile}
                    onChange={(e) => setFormData({ ...formData, keyFile: e.target.value })}
                    rows={6}
                    placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                    className="code-textarea"
                  />
                </div>
              </>
            )}
          </div>

          {/* Additional Information Section */}
          <div className="form-section">
            <div className="section-header">
              <Settings size={18} />
              <h3>Additional Information</h3>
            </div>

            <div className="form-group">
              <label>
                <Tag size={14} />
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="production, critical, database, web-server"
              />
              <div className="field-hint">
                <Info size={12} />
                <span>Use tags to organize and filter your credentials</span>
              </div>
            </div>

            <div className="form-group">
              <label>
                <FileText size={14} />
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                placeholder="Additional notes, documentation, or important information about this credential"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? (
                <>
                  <RefreshCw size={16} className="spinning" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Key size={16} />
                  <span>{credential ? 'Update Credential' : 'Create Credential'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CredentialModal;
